import ClearIcon from "@diligentcorp/atlas-react-icons/Clear.js";
import ColumnsIcon from "@diligentcorp/atlas-react-icons/Columns.js";
import DensityComfortableIcon from "@diligentcorp/atlas-react-icons/DensityComfortable.js";
import DensityCompactIcon from "@diligentcorp/atlas-react-icons/DensityCompact.js";
import DensityStandardIcon from "@diligentcorp/atlas-react-icons/DensityStandard.js";
import ExportIcon from "@diligentcorp/atlas-react-icons/Export.js";
import FilterIcon from "@diligentcorp/atlas-react-icons/Filter.js";
import MoreIcon from "@diligentcorp/atlas-react-icons/More.js";
import SearchIcon from "@diligentcorp/atlas-react-icons/Search.js";
import TableSortAscendingIcon from "@diligentcorp/atlas-react-icons/TableSortAscending.js";
import TableSortDescendingIcon from "@diligentcorp/atlas-react-icons/TableSortDescending.js";
const getDataGridTokens = (tokens) => {
    return {
        table: tokens.component.table,
        coreColor: tokens.core.color,
        semanticColor: tokens.semantic.color,
        borderWidth: tokens.semantic.borderWidth,
        spacing: tokens.semantic.spacing,
        radius: tokens.semantic.radius,
        coreShadow: tokens.core.shadow,
        semanticShadow: tokens.semantic.shadow,
    };
};
const getHeightFromString = (string) => parseInt(string, 10);
const grid = (tokens) => {
    const { table, spacing, radius, coreShadow, semanticShadow, coreColor, semanticColor, borderWidth, } = getDataGridTokens(tokens);
    const sizes = {
        row: getHeightFromString(table.row.height.value),
        compactRow: getHeightFromString(table.compact.row.height.value),
        header: getHeightFromString(table.header.height.value),
    };
    return {
        MuiDataGrid: {
            defaultProps: {
                rowHeight: sizes.row,
                headerHeight: sizes.header,
                getRowClassName: (params) => params.indexRelativeToCurrentPage % 2 === 0 ? "MuiEven" : "MuiOdd",
                components: {
                    // Note that these are overwritten by custom Pagination
                    ColumnSortedAscendingIcon: TableSortAscendingIcon,
                    ColumnSortedDescendingIcon: TableSortDescendingIcon,
                    ColumnMenuIcon: MoreIcon,
                    ColumnFilteredIcon: FilterIcon,
                    DensityCompactIcon: DensityCompactIcon,
                    DensityComfortableIcon: DensityComfortableIcon,
                    DensityStandardIcon: DensityStandardIcon,
                    ExportIcon: ExportIcon,
                    FilterPanelDeleteIcon: ClearIcon,
                    OpenFilterButtonIcon: FilterIcon,
                    ColumnSelectorIcon: ColumnsIcon,
                    QuickFilterClearIcon: ClearIcon,
                    QuickFilterIcon: SearchIcon,
                },
                componentsProps: {
                    // Setting inputs used within the filter panel to outlined
                    baseSelect: {
                        variant: "outlined",
                        size: "medium",
                    },
                    baseTextField: {
                        variant: "outlined",
                        size: "medium",
                    },
                    baseButton: {
                        variant: "text",
                        size: "medium",
                    },
                },
            },
            styleOverrides: {
                root: {
                    fontSize: table.cell.fontSize.value,
                    borderRadius: table.borderRadius.value,
                    ".MuiDataGrid-main": {
                        borderRadius: table.borderRadius.value,
                    },
                    ".MuiDataGrid-toolbarContainer": {
                        padding: `${spacing.default.value} 0`,
                        gap: spacing.default.value,
                        "& .MuiTextField-root": {
                            paddingBottom: 0,
                        },
                    },
                    ".MuiDataGrid-columnHeaders": {
                        backgroundColor: table.header.backgroundColor.value,
                        borderRadius: 0,
                        ".MuiDataGrid-columnHeader": {
                            borderWidth: 0,
                            color: table.header.color.value,
                            fontSize: table.header.fontSize.value,
                            lineHeight: table.header.lineHeight.value,
                            paddingLeft: table.header.paddingHorizontal.value,
                            paddingRight: table.header.paddingHorizontal.value,
                            "&:first-of-type": {
                                paddingLeft: table.header.first.paddingLeft.value,
                                "&.MuiDataGrid-columnHeaderCheckbox": {
                                    boxSizing: "content-box",
                                    paddingLeft: spacing.lg.value,
                                    paddingRight: 0,
                                    // Header checkboxes always have a fixed width,
                                    // so they cannot be resized
                                    ".MuiDataGrid-columnSeparator": {
                                        display: "none",
                                    },
                                    ".MuiDataGrid-columnHeaderTitleContainerContent": {
                                        overflow: "visible",
                                    },
                                },
                            },
                            "&:last-of-type": {
                                paddingRight: table.header.last.paddingRight.value,
                            },
                            "&:focus": {
                                borderRadius: radius.sm.value,
                                outline: "none",
                                boxShadow: semanticShadow.focusInset.value.stringValue,
                            },
                            "&:focus-within": {
                                outline: "none",
                            },
                            // This is the column resizer element
                            ".MuiDataGrid-columnSeparator": {
                                color: coreColor.legacy.light.value,
                                "&:hover": {
                                    color: semanticColor.action.hover.value,
                                },
                            },
                            // This opens the menu from the column header
                            ".MuiDataGrid-menuIcon": {
                                width: "auto",
                                marginRight: 0,
                                ".MuiDataGrid-menuIconButton": {
                                    visibility: "visible",
                                },
                            },
                            ".MuiIconButton-root": {
                                color: table.header.iconButton.color.value,
                                "&:active": {
                                    color: table.header.iconButton.active.color.value,
                                },
                            },
                            ".MuiDataGrid-columnHeaderTitleContainer": {
                                gap: spacing.default.value,
                                overflow: "visible",
                            },
                            "&.MuiDataGrid-columnHeader--moving": {
                                backgroundColor: table.row.selected.backgroundColor.value,
                            },
                        },
                    },
                    "&.atlas-style-divided .MuiDataGrid-row": {
                        borderBottom: `${borderWidth.thin.value} solid ${table.cell.borderColor.value}`,
                        "&:hover, &.Mui-hovered": {
                            backgroundColor: table.row.zebraStripe.backgroundColor.value,
                        },
                        "&.Mui-selected, &.Mui-selected:hover, &.Mui-selected.Mui-hovered": {
                            backgroundColor: table.row.selected.backgroundColor.value,
                        },
                        "&:nth-of-type(even)": {
                            borderBottom: `${borderWidth.thin.value} solid ${table.cell.borderColor.value}`,
                        },
                    },
                    "&:not(.atlas-style-divided) .MuiDataGrid-row": {
                        borderBottom: "none",
                        backgroundColor: table.row.backgroundColor.value,
                        "&.Mui-hovered, &:hover": {
                            backgroundColor: table.row.hover.backgroundColor.value,
                        },
                        "&.MuiOdd:not([data-atlas-style-no-zebra-stripes])": {
                            borderBottom: "none",
                            backgroundColor: table.row.zebraStripe.backgroundColor.value,
                            "&.Mui-hovered, &:hover": {
                                backgroundColor: table.row.hover.backgroundColor.value,
                            },
                        },
                        "&.Mui-selected": {
                            backgroundColor: table.row.selected.backgroundColor.value,
                            "&.Mui-hovered, &:hover": {
                                backgroundColor: table.row.selected.backgroundColor.value,
                            },
                        },
                    },
                    ".MuiDataGrid-cell": {
                        borderWidth: 0,
                        padding: 0,
                        fontSize: table.cell.fontSize.value,
                        lineHeight: table.cell.lineHeight.value,
                        "& .MuiDataGrid-cellContent, .MuiDataGrid-actionsCell": {
                            paddingLeft: table.cell.paddingHorizontal.value,
                            paddingRight: table.cell.paddingHorizontal.value,
                        },
                        "&:first-of-type": {
                            "& .MuiDataGrid-cellContent": {
                                paddingLeft: table.cell.first.paddingLeft.value,
                            },
                            "&.MuiDataGrid-cellCheckbox": {
                                boxSizing: "content-box",
                                paddingLeft: spacing.lg.value,
                                paddingRight: 0,
                            },
                        },
                        "&:last-of-type .MuiDataGrid-cellContent": {
                            paddingRight: table.cell.last.paddingRight.value,
                        },
                        "&:focus": {
                            borderColor: semanticColor.ui.focusRing.value,
                            borderRadius: radius.sm.value,
                            outline: "none",
                            boxShadow: semanticShadow.focusInset.value.stringValue,
                        },
                        "&:focus-within": {
                            outline: "none",
                        },
                        ".MuiCheckbox-root": {
                            alignSelf: "center",
                        },
                    },
                    // Pinned headers
                    ".MuiDataGrid-pinnedColumnHeaders, .MuiDataGrid-pinnedColumns": {
                        boxShadow: coreShadow.low.value.stringValue,
                    },
                    ".MuiDataGrid-pinnedColumnHeaders": {
                        backgroundColor: table.header.backgroundColor.value,
                        "&.MuiDataGrid-pinnedColumnHeaders--left": {
                            borderRight: `${borderWidth.thin.value} solid ${table.cell.borderColor.value}`,
                            "& .MuiDataGrid-columnHeader:last-of-type": {
                                paddingRight: table.header.paddingHorizontal.value,
                            },
                        },
                        "&.MuiDataGrid-pinnedColumnHeaders--right": {
                            borderLeft: `${borderWidth.thin.value} solid ${table.cell.borderColor.value}`,
                            "& .MuiDataGrid-columnHeader:first-of-type": {
                                paddingLeft: table.header.paddingHorizontal.value,
                            },
                        },
                    },
                    ".MuiDataGrid-pinnedColumns": {
                        "&.MuiDataGrid-pinnedColumns--left": {
                            borderRight: `${borderWidth.thin.value} solid ${table.cell.borderColor.value}`,
                        },
                        "&.MuiDataGrid-pinnedColumns--right": {
                            borderLeft: `${borderWidth.thin.value} solid ${table.cell.borderColor.value}`,
                        },
                    },
                    "& .MuiDataGrid-footerContainer": {
                        "& .MuiTablePagination-toolbar": {
                            padding: `${spacing.default.value} 0`,
                            overflow: "visible",
                        },
                        "& .MuiDataGrid-selectedRowCount": {
                            marginLeft: 0,
                        },
                    },
                },
                panelContent: {
                    padding: `${spacing.md.value} 0`,
                },
                filterForm: {
                    gap: spacing.default.value,
                    padding: `${spacing.sm.value} ${spacing.md.value}`,
                    "& fieldset.MuiOutlinedInput-notchedOutline": {
                        top: 0,
                        "& legend": {
                            display: "none",
                        },
                    },
                    "& .MuiDataGrid-filterFormOperatorInput, .MuiDataGrid-filterFormColumnInput": {
                        width: "auto",
                        minWidth: "120px",
                    },
                    // This is an arbitrary padding value to center the filter delete button with the row
                    "& .MuiDataGrid-filterFormDeleteIcon": {
                        paddingBottom: "6px",
                    },
                },
                paper: {
                    // This is used in the filter panel
                    boxShadow: coreShadow.medium.value.stringValue,
                    borderRadius: radius.md.value,
                },
                menu: {
                    "& .MuiPaper-root": {
                        boxShadow: coreShadow.medium.value.stringValue,
                        borderRadius: radius.md.value,
                    },
                },
                panelFooter: {
                    padding: spacing.default.value,
                    borderTop: `${borderWidth.thin.value} solid ${semanticColor.ui.divider.value}`,
                },
            },
        },
    };
};
export default grid;
//# sourceMappingURL=dataGrid.js.map